import { Link } from "@tanstack/react-router";
import { Mail, Phone, MapPin, AlertTriangle, Facebook, Linkedin, Building2 } from "lucide-react";
import { site, companyRegLine } from "@/lib/site";
import { services } from "@/lib/services-data";
import { NewsletterForm } from "./NewsletterForm";
import logoAsset from "@/assets/logo.jpg.asset.json";
import logo48 from "@/assets/logo-48.webp.asset.json";
import logo96 from "@/assets/logo-96.webp.asset.json";
import logo144 from "@/assets/logo-144.webp.asset.json";
import logo48Avif from "@/assets/logo-48.avif.asset.json";
import logo96Avif from "@/assets/logo-96.avif.asset.json";
import logo144Avif from "@/assets/logo-144.avif.asset.json";

export function Footer() {
  return (
    <footer className="mt-24 bg-navy text-navy-foreground">
      <div className="container-narrow grid gap-12 py-16 md:grid-cols-4">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center overflow-hidden rounded-md bg-gold/10">
              <picture>
                <source type="image/avif" srcSet={`${logo48Avif.url} 1x, ${logo96Avif.url} 2x, ${logo144Avif.url} 3x`} />
                <source type="image/webp" srcSet={`${logo48.url} 1x, ${logo96.url} 2x, ${logo144.url} 3x`} />
                <img src={logoAsset.url} alt="" width={44} height={44} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </picture>
            </span>
            <span className="font-display text-lg font-semibold">
              Nkadimeng Danny
            </span>
          </div>
          <p className="mt-4 text-sm text-navy-foreground/70 leading-relaxed">
            Affordable, accessible legal support and compliance services for
            individuals, employees, employers and businesses across South Africa.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <a
              href={site.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="grid h-9 w-9 place-items-center rounded-full border border-navy-foreground/15 text-navy-foreground/75 transition-colors hover:border-gold hover:text-gold"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href={site.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="grid h-9 w-9 place-items-center rounded-full border border-navy-foreground/15 text-navy-foreground/75 transition-colors hover:border-gold hover:text-gold"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gold">
            Services
          </h2>
          <ul className="mt-4 space-y-2 text-sm">
            {services.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: s.slug }}
                  className="text-navy-foreground/75 hover:text-gold transition-colors"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gold">
            Explore
          </h2>
          <ul className="mt-4 space-y-2 text-sm">
            {[
              ["/education", "Education Division"],
              ["/corporate-training", "Corporate training"],
              ["/attorneys", "Attorney partnerships"],
              ["/about", "About"],
              ["/resources", "Resource centre"],
              ["/book", "Book consultation"],
              ["/faq", "FAQ"],
              ["/contact", "Contact"],
              ["/disclaimer", "Disclaimer & POPIA"],
              ["/privacy", "Privacy policy"],
              ["/popia", "POPIA policy"],
              ["/terms", "Terms of service"],
              ["/complaints", "Complaints procedure"],
            ].map(([to, label]) => (
              <li key={to}>
                <Link
                  to={to}
                  className="text-navy-foreground/75 hover:text-gold transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
          <h2 className="mt-6 text-sm font-semibold uppercase tracking-wider text-gold">
            Contact
          </h2>
          <ul className="mt-3 space-y-3 text-sm text-navy-foreground/80">
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 text-gold" />
              <a href={`tel:${site.phoneIntl}`} className="hover:text-gold">
                {site.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-gold" />
              <a href={`mailto:${site.email}`} className="hover:text-gold break-all">
                {site.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-gold" />
              <span>
                {site.address.line1}, {site.address.line2},{" "}
                {site.address.province}, {site.address.postalCode}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Building2 className="h-4 w-4 mt-0.5 text-gold" />
              <span>
                {site.registration.entityType}
                <br />
                <span className="text-navy-foreground/60">
                  {companyRegLine()}
                </span>
              </span>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gold">
            Newsletter
          </h2>
          <p className="mt-4 text-sm text-navy-foreground/75">
            Legal insights and compliance updates, straight to your inbox.
          </p>
          <div className="mt-4">
            <NewsletterForm />
          </div>
        </div>
      </div>

      <div className="border-t border-navy-foreground/10">
        <div className="container-narrow space-y-3 py-6 text-xs text-navy-foreground/70">
          <div className="flex items-start gap-2 rounded-md bg-navy-foreground/5 p-3 leading-relaxed">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p>
              <strong className="text-navy-foreground">Important:</strong>{" "}
              Nkadimeng Danny is an LLB graduate and is{" "}
              <strong className="text-navy-foreground">not an admitted
              attorney</strong> of the High Court of South Africa. We{" "}
              <strong className="text-navy-foreground">do provide legal
              information, legal advice on non-reserved matters and legal
              opinions</strong>, alongside legal support, paralegal and
              compliance services. We do not undertake work reserved by the
              Legal Practice Act 28 of 2014 and we do not represent clients in
              court. We do not operate a trust account and do not hold a
              Fidelity Fund Certificate. Please read our{" "}

              <Link to="/disclaimer" className="text-gold underline hover:text-navy-foreground">
                full disclaimer &amp; POPIA notice
              </Link>.
            </p>
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <p>
              © {new Date().getFullYear()} {site.name} ({site.registration.number}). All rights reserved.
            </p>
            <p className="flex flex-wrap items-center gap-3 text-navy-foreground/60">
              <span>POPIA aligned · Confidential · Professional</span>
              <Link
                to="/lexpractiq"
                className="rounded border border-gold/40 bg-gold/10 px-2 py-0.5 font-semibold text-gold hover:bg-gold hover:text-navy"
              >
                Try SA LexPractiq →
              </Link>
              <Link
                to="/auth"
                className="rounded border border-navy-foreground/20 px-2 py-0.5 hover:border-gold hover:text-gold"
              >
                Admin sign-in
              </Link>
            </p>
          </div>

          <p className="text-[11px] text-navy-foreground/50">
            Registered with the Companies and Intellectual Property Commission
            (CIPC) of the Republic of South Africa. Enterprise no.{" "}
            {site.registration.enterpriseNumber} · Incorporated{" "}
            {site.registration.incorporatedOn} · Financial year-end{" "}
            {site.registration.financialYearEnd}.
          </p>

        </div>
      </div>
    </footer>
  );
}
